/*
  GD Scroll Video Hero
  - Autoplays the scrub video 0 -> introEnd on load, then pauses.
  - Maps scroll progress through the section to video time across the
    remaining range (introEnd -> scrubEnd/duration), weighted by each
    step's scroll length. Steps with data-scrub="false" hold the frame.
  - Seeks are rAF-throttled and snapped to the configured frame rate so
    stop-motion footage steps cleanly.
  - Per-step overlay videos play/pause on step activation.
*/
if (!customElements.get('gd-scroll-video-hero')) {
  customElements.define(
    'gd-scroll-video-hero',
    class GDScrollVideoHero extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.gdsvh__track');
        this.stage = this.querySelector('.gdsvh__stage');
        this.video = this.querySelector('.gdsvh__video');
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        if (!this.track || this.steps.length === 0) return;

        this.introEnd = parseFloat(this.dataset.introEnd) || 0;
        this.fps = Math.max(parseInt(this.dataset.fps, 10) || 12, 1);
        this.scrubEndSetting = parseFloat(this.dataset.scrubEnd) || 0;
        this.fadeAt = (parseFloat(this.dataset.fadeAt) || 33) / 100;
        this.leads = this.steps.map((s) => (parseFloat(s.dataset.scrubLead) || 0) / 100);
        this.overlayExits = this.steps.map((s) => (parseFloat(s.dataset.overlayExit) || 0) / 100);
        this.scrollDuration = parseInt(this.dataset.scrollDuration, 10) || 800;
        // A real user scroll cancels any in-flight button animation.
        ['wheel', 'touchstart'].forEach((ev) =>
          window.addEventListener(ev, () => cancelAnimationFrame(this.scrollAnim), { passive: true })
        );
        this.cards = this.steps.map((s) => s.querySelector('.gdsvh__card'));
        this.measureCards();
        /* Card height changes with fonts loading, content edits in the theme
           editor, or a reflow at a new width — re-measure rather than trusting
           the first read. Margin changes don't alter the observed border-box,
           so this can't feed back on itself. */
        if ('ResizeObserver' in window) {
          this.cardObserver = new ResizeObserver(() => this.measureCards());
          this.cards.forEach((c) => c && this.cardObserver.observe(c));
        }

        this.state = this.video ? 'waiting' : 'scrub'; // waiting -> intro -> scrub
        this.activeIndex = -1;
        this.overlayIndex = -1;
        this.targetTime = this.introEnd;
        this.ticking = false;
        this.inView = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.overlays = new Map();
        this.querySelectorAll('[data-step-overlay]').forEach((el) => {
          this.overlays.set(parseInt(el.dataset.stepOverlay, 10), el);
          const vid = el.querySelector('video');
          if (vid) vid.loop = el.dataset.loop === 'true';
          const toggle = el.querySelector('[data-sound-toggle]');
          if (toggle) {
            toggle.addEventListener('click', () => {
              const v = el.querySelector('video');
              v.muted = !v.muted;
              el.classList.toggle('gdsvh__overlay--unmuted', !v.muted);
            });
          }
        });

        this.computeSegments();
        this.bind();
        this.setupVideo();
        this.onScroll();
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.resizeHandler);
        if (this.cardObserver) this.cardObserver.disconnect();
      }

      /* ---------- setup ---------- */

      /* Reserves each card's half-height as top margin, cancelling the upward
         pull of translateY(-50%) so a card never renders above its step's
         start. See the --gdsvh-card-half note in the stylesheet. */
      measureCards() {
        this.cards.forEach((card, i) => {
          if (!card || !this.steps[i]) return;
          this.steps[i].style.setProperty('--gdsvh-card-half', card.offsetHeight / 2 + 'px');
        });
        /* The opt-in intro card is a .gdsvh__step too, but has no data-step,
           so it isn't in this.steps. */
        const intro = this.querySelector('.gdsvh__intro');
        const introCard = intro && intro.querySelector('.gdsvh__card');
        if (introCard) {
          intro.style.setProperty('--gdsvh-card-half', introCard.offsetHeight / 2 + 'px');
        }
      }

      computeSegments() {
        const lengths = this.steps.map((s) => parseFloat(s.dataset.scrollLength) || 100);
        const total = lengths.reduce((a, b) => a + b, 0);
        const weights = this.steps.map((s, i) => (s.dataset.scrub === 'true' ? lengths[i] : 0));
        const weightTotal = weights.reduce((a, b) => a + b, 0) || 1;

        let pCursor = 0;
        let wCursor = 0;
        this.segments = this.steps.map((step, i) => {
          const seg = {
            start: pCursor,
            end: pCursor + lengths[i] / total,
            vStart: wCursor / weightTotal,
            vEnd: (wCursor + weights[i]) / weightTotal,
            scrub: weights[i] > 0,
          };
          pCursor = seg.end;
          wCursor += weights[i];
          return seg;
        });
      }

      bind() {
        this.scrollHandler = () => this.onScroll();
        this.resizeHandler = () => {
          this.measureCards();
          this.computeSegments();
          this.onScroll();
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
        window.addEventListener('resize', this.resizeHandler);

        this.io = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
            if (this.inView) {
              this.maybeStartIntro();
              this.onScroll();
            } else if (this.video && this.state === 'intro') {
              this.video.pause();
            }
          },
          { rootMargin: '20% 0px' }
        );
        this.io.observe(this);

        this.querySelectorAll('[data-next]').forEach((btn, idx) => {
          // data-next buttons appear in step order; find owning step index.
          const step = btn.closest('[data-step]');
          const i = this.steps.indexOf(step);
          btn.addEventListener('click', () => this.scrollToStep(i + 1));
        });

        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.scrollToStep(i)));

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            const step = e.target && e.target.closest && e.target.closest('[data-step]');
            const i = this.steps.indexOf(step);
            if (i >= 0) this.scrollToStep(i, 'auto');
          });
        }
      }

      setupVideo() {
        if (!this.video) return;
        const onReady = () => {
          this.duration = this.video.duration;
          this.scrubEnd =
            this.scrubEndSetting > 0 && this.scrubEndSetting > this.introEnd
              ? Math.min(this.scrubEndSetting, this.duration)
              : this.duration;
          this.introEnd = Math.min(this.introEnd, this.scrubEnd);
          this.maybeStartIntro();
        };
        if (this.video.readyState >= 1) onReady();
        else this.video.addEventListener('loadedmetadata', onReady, { once: true });
      }

      maybeStartIntro() {
        // Behind an opt-in intro the scrub video is hidden, so playing the
        // 0 -> introEnd lead-in now would burn it off unseen and reveal the
        // end frame on enter. The section's gate script clears data-gated and
        // calls back in once the visitor opts in.
        if (this.dataset.gated === 'true') return;
        if (!this.video || this.state !== 'waiting' || !this.inView || !this.duration) return;
        if (this.reducedMotion || this.introEnd <= 0) {
          this.state = 'scrub';
          this.video.currentTime = this.snap(this.introEnd);
          return;
        }
        this.state = 'intro';
        const tick = () => {
          if (this.state !== 'intro') return;
          if (this.video.currentTime >= this.introEnd) {
            this.video.pause();
            this.video.currentTime = this.snap(this.introEnd);
            this.state = 'scrub';
            return;
          }
          requestAnimationFrame(tick);
        };
        this.video
          .play()
          .then(() => requestAnimationFrame(tick))
          .catch(() => {
            // Autoplay blocked (e.g. iOS Low Power Mode) — fall back to scrub.
            this.state = 'scrub';
          });
      }

      /* ---------- scroll loop ---------- */

      progress(offsetPx = 0) {
        const rect = this.track.getBoundingClientRect();
        const scrollable = rect.height - window.innerHeight;
        if (scrollable <= 0) return 0;
        return Math.min(Math.max((-rect.top + offsetPx) / scrollable, 0), 1);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          if (!this.inView) return;
          const p = this.progress();

          // A real scroll cancels the intro and hands control to the scrubber.
          if (this.state === 'intro' && p > 0.01) {
            this.video.pause();
            this.state = 'scrub';
          }

          this.updateActiveStep(p);
          this.updateFades();
          this.updateDots();

          // Shifted windows (segment starts pulled earlier by each step's
          // scrub lead) drive BOTH video time and overlay visibility, so a
          // hold step's overlay arrives in sync with its frozen frame.
          const scrollable = Math.max(this.track.offsetHeight - window.innerHeight, 1);
          const px = p * scrollable;
          const starts = this.windowStarts(scrollable);
          const wIdx = this.windowIndex(px, starts);
          this.updateOverlays(wIdx);
          if (this.video && this.state === 'scrub' && this.duration) {
            this.targetTime = this.timeForWindow(px, scrollable, starts, wIdx);
            this.applySeek();
          }
        });
      }

      /* Per-step window starts: each segment boundary pulled earlier by that
         step's scrub lead (% of viewport). Leads are capped at 75% of the
         previous segment's scroll distance so a large lead can never collapse
         the previous step's window into a zero-width jump — it compresses it
         instead. Clamped monotonic. */
      windowStarts(scrollable) {
        const starts = [];
        let prev = -Infinity;
        for (let i = 0; i < this.segments.length; i++) {
          let leadPx = this.leads[i] * window.innerHeight;
          if (i > 0) {
            const prevSegPx = (this.segments[i].start - this.segments[i - 1].start) * scrollable;
            leadPx = Math.min(leadPx, prevSegPx * 0.75);
          }
          let b = this.segments[i].start * scrollable - leadPx;
          if (b < prev) b = prev;
          prev = b;
          starts.push(b);
        }
        return starts;
      }

      windowIndex(px, starts) {
        let idx = starts.length - 1;
        for (let i = 0; i < starts.length - 1; i++) {
          if (px < starts[i + 1]) {
            idx = i;
            break;
          }
        }
        return idx;
      }

      timeForWindow(px, scrollable, starts, idx) {
        const span = this.scrubEnd - this.introEnd;
        const winStart = starts[idx];
        const winEnd = idx + 1 < starts.length ? starts[idx + 1] : scrollable;
        const seg = this.segments[idx];
        const local = winEnd > winStart ? Math.min(Math.max((px - winStart) / (winEnd - winStart), 0), 1) : 1;
        const vFrac = seg.scrub ? seg.vStart + local * (seg.vEnd - seg.vStart) : seg.vStart;
        return this.introEnd + vFrac * span;
      }

      snap(t) {
        return Math.round(t * this.fps) / this.fps;
      }

      applySeek() {
        if (this.video.seeking) {
          // Try again next frame with the latest target.
          if (!this.seekQueued) {
            this.seekQueued = true;
            this.video.addEventListener(
              'seeked',
              () => {
                this.seekQueued = false;
                this.applySeek();
              },
              { once: true }
            );
          }
          return;
        }
        const snapped = this.snap(this.targetTime);
        if (Math.abs(this.video.currentTime - snapped) >= 1 / (this.fps * 2)) {
          this.video.currentTime = snapped;
        }
      }

      /* ---------- steps + overlays ---------- */

      updateActiveStep(p) {
        let active = 0;
        for (let i = 0; i < this.segments.length; i++) {
          if (p >= this.segments[i].start) active = i;
        }
        if (active === this.activeIndex) return;
        this.activeIndex = active;
        this.dataset.activeStep = String(active);
        this.steps.forEach((s, i) => s.classList.toggle('is-active', i === active));
      }

      /* Dots track the cards' real pin positions (not segment boundaries),
         switching slightly early — when a card is within 15% of the viewport
         of reaching its sticky point. */
      updateDots() {
        if (!this.dots.length) return;
        const early = window.innerHeight * 0.15;
        const tops = this.steps.map((s) => s.getBoundingClientRect().top);
        const stickies = this.cards.map((c) => parseFloat(getComputedStyle(c).top) || 0);
        let current = 0;
        for (let i = 0; i < this.steps.length; i++) {
          if (tops[i] <= stickies[i] + early) current = i;
        }
        // Fill = progress from this dot's activation point to the next one's
        // (or to the end of the track for the last step).
        const activated = stickies[current] + early - tops[current];
        let span;
        if (current + 1 < this.steps.length) {
          span = tops[current + 1] - tops[current];
        } else {
          const trackRect = this.track.getBoundingClientRect();
          span = trackRect.bottom - window.innerHeight - tops[current] + stickies[current] + early;
        }
        const fill = span > 0 ? Math.min(Math.max(activated / span, 0), 1) : 1;
        this.dots.forEach((d, i) => {
          d.classList.toggle('is-current', i === current);
          d.style.setProperty('--gdsvh-dot-fill', i === current ? (fill * 100).toFixed(1) + '%' : '0%');
        });
      }

      /* Overlays follow the lead-shifted windows rather than the card
         segments, so a step's overlay can arrive early with its scrub lead. */
      updateOverlays(wIdx) {
        if (wIdx !== this.overlayIndex) {
          if (this.overlayIndex >= 0) this.deactivateOverlay(this.overlayIndex);
          this.overlayIndex = wIdx;
          this.activateOverlay(wIdx);
        }
        // Early exit: fade the current overlay out once the NEXT step's card
        // has risen past this step's exit trigger. Reverses on scroll-up.
        const el = this.overlays.get(this.overlayIndex);
        const exit = this.overlayExits[this.overlayIndex] || 0;
        if (!el || exit <= 0 || this.overlayIndex + 1 >= this.steps.length) return;
        const nextTop = this.cards[this.overlayIndex + 1].getBoundingClientRect().top;
        const out = nextTop < window.innerHeight * (1 - exit);
        if (out && !el.classList.contains('is-exited')) {
          el.classList.add('is-exited');
          this.deactivateOverlay(this.overlayIndex);
        } else if (!out && el.classList.contains('is-exited')) {
          el.classList.remove('is-exited');
          this.activateOverlay(this.overlayIndex);
        }
      }

      /* A card fades out once the NEXT card has risen `fadeAt` of the way up
         from the bottom of the viewport — measured from the incoming card's
         real position so the handoff tracks the visual overlap, not just the
         segment boundary. Runs every frame (scrolling up reverses it). */
      updateFades() {
        const active = this.activeIndex;
        const triggerY = window.innerHeight * (1 - this.fadeAt);
        this.steps.forEach((s, i) => {
          let faded = i < active;
          if (i === active && i + 1 < this.steps.length) {
            const nextCard = this.cards[i + 1];
            if (nextCard && nextCard.getBoundingClientRect().top < triggerY) faded = true;
          }
          s.classList.toggle('is-past', faded);
        });
      }

      activateOverlay(i) {
        const el = this.overlays.get(i);
        if (!el) return;
        el.classList.remove('is-exited');
        el.hidden = false;
        requestAnimationFrame(() => el.classList.add('is-visible'));
        const v = el.querySelector('video');
        if (el.dataset.autoplay === 'true' && !this.reducedMotion) {
          v.currentTime = 0;
          v.play().catch(() => {});
        }
      }

      deactivateOverlay(i) {
        const el = this.overlays.get(i);
        if (!el) return;
        el.classList.remove('is-visible');
        const v = el.querySelector('video');
        v.pause();
        if (el.dataset.autoplay === 'true') {
          v.muted = true;
          el.classList.remove('gdsvh__overlay--unmuted');
        }
        setTimeout(() => {
          if (!el.classList.contains('is-visible')) el.hidden = true;
        }, 350);
      }

      /* ---------- navigation ---------- */

      scrollToStep(i, behavior = 'smooth') {
        const rect = this.track.getBoundingClientRect();
        const top = rect.top + window.scrollY;
        const scrollable = rect.height - window.innerHeight;
        let target;
        if (i >= this.segments.length) {
          target = top + rect.height; // past the section
        } else {
          // Land where the step is BOTH active (segment start) and its card
          // is settled at the sticky pin point — whichever is further down.
          const segStart = top + this.segments[i].start * scrollable;
          const stepTop = this.steps[i].getBoundingClientRect().top + window.scrollY;
          const stickyTop = parseFloat(getComputedStyle(this.cards[i]).top) || 0;
          const pin = stepTop - stickyTop;
          target = Math.min(Math.max(segStart, pin) + 2, top + scrollable);
        }
        if (behavior !== 'smooth' || this.reducedMotion) {
          window.scrollTo({ top: target });
          return;
        }
        this.animateScrollTo(target, this.scrollDuration);
      }

      animateScrollTo(target, duration) {
        cancelAnimationFrame(this.scrollAnim);
        const from = window.scrollY;
        const delta = target - from;
        const start = performance.now();
        // Linear on purpose: a constant scroll rate means a constant video
        // scrub rate, so the footage plays evenly instead of lurching.
        const frame = (now) => {
          const t = Math.min((now - start) / duration, 1);
          window.scrollTo(0, from + delta * t);
          if (t < 1) this.scrollAnim = requestAnimationFrame(frame);
        };
        this.scrollAnim = requestAnimationFrame(frame);
      }
    }
  );
}
